import React, { useState, useEffect, useRef } from 'react';
import {
  loadGoogleApis, isDriveConfigured,
  uploadFileToDrive, listStudentFiles, deleteFileFromDrive,
  fetchImageAsBlob, getDriveError,
} from '../../lib/googleDrive';

const PASSPORT_FILENAME = 'Passport_Photo.jpg';

export default function PassportPhotoUpload({ student }) {
  const [photoUrl, setPhotoUrl]       = useState(null);
  const [photoFileId, setPhotoFileId] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [configured, setConfigured]   = useState(false);
  const [checking, setChecking]     = useState(true);   // PHOTO-CHECK
  const [reason, setReason]         = useState('');
  const [error, setError]             = useState('');
  const fileInputRef                  = useRef(null);

  const studentName = student?.full_name || 'Unknown';
  const regNo       = student?.register_number || 'UNKNOWN';

  useEffect(() => {
    let cancelled = false;
    setChecking(true); setReason('');
    // never wait more than 15 seconds for Google to answer
    const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error('Google did not respond (timed out)')), 15000));
    Promise.race([
      loadGoogleApis().then(() => isDriveConfigured()),
      timeout,
    ]).then(ok => {
      if (cancelled) return;
      setConfigured(!!ok);
      if (!ok) setReason(getDriveError() || 'Drive is not connected.');
      setChecking(false);
      if (ok) loadPhoto();
    }).catch(err => {
      if (cancelled) return;
      console.error('Passport photo: Drive check failed', err);
      setConfigured(false);
      setReason((err && err.message) || 'Could not reach Google Drive.');
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [student]);

  async function loadPhoto() {
    if (!student) return;
    setLoading(true);
    try {
      const files = await listStudentFiles(studentName, regNo);
      const photo = files.find(f => f.name === PASSPORT_FILENAME || f.mimeType?.startsWith('image/'));
      if (photo) {
        setPhotoFileId(photo.id);
        try {
          const blobUrl = await fetchImageAsBlob(photo.id);
          setPhotoUrl(blobUrl);
        } catch {
          setPhotoUrl(`https://drive.google.com/thumbnail?id=${photo.id}&sz=w300`);
        }
      } else {
        setPhotoUrl(null);
        setPhotoFileId(null);
      }
    } catch { setError('Error loading photo.'); }
    setLoading(false);
  }

  async function handlePhotoSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    setUploading(true); setUploadProgress(0); setError('');
    try {
      if (photoFileId) await deleteFileFromDrive(photoFileId);
      const renamedFile = new File([file], PASSPORT_FILENAME, { type: file.type });
      const uploaded = await uploadFileToDrive(renamedFile, studentName, regNo, setUploadProgress);
      setPhotoFileId(uploaded.id);
      setTimeout(async () => {
        try {
          const blobUrl = await fetchImageAsBlob(uploaded.id);
          setPhotoUrl(blobUrl);
        } catch {
          setPhotoUrl(`https://drive.google.com/thumbnail?id=${uploaded.id}&sz=w300`);
        }
      }, 1000);
      setError('');
    } catch (err) { setError('Upload failed. Please try again.'); }
    setUploading(false); setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleDelete() {
    if (!window.confirm('Remove passport photo?')) return;
    try {
      if (photoFileId) await deleteFileFromDrive(photoFileId);
      setPhotoUrl(null); setPhotoFileId(null);
    } catch { setError('Delete failed.'); }
  }

  if (!configured) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 120, height: 150, borderRadius: 8, border: '2px dashed var(--border)', background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: 10 }}>
            <div style={{ fontSize: 32 }}>👤</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{checking ? 'Loading...' : 'Drive not connected'}</div>
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)' }}>Passport Photo</div>
        {!checking && <div style={{ fontSize: 10, color: 'var(--red)', textAlign: 'center', maxWidth: 160 }}>{reason || 'Drive not set up. Contact admin.'}</div>}
        {!checking && <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => window.location.reload()}>🔄 Retry</button>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 120, height: 150, borderRadius: 8,
        border: '2px dashed var(--border)', background: 'var(--bg3)',
        overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', cursor: 'pointer',
      }}
        onClick={() => fileInputRef.current?.click()}
        title="Click to upload photo"
      >
        {loading ? (
          <div className="spinner" style={{ width: 24, height: 24 }} />
        ) : photoUrl ? (
          <img src={photoUrl} alt="Passport" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setPhotoUrl(null)} />
        ) : (
          <div style={{ textAlign: 'center', padding: 10 }}>
            <div style={{ fontSize: 32 }}>👤</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>Click to upload</div>
          </div>
        )}
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{uploadProgress}%</div>
            <div style={{ width: 80, height: 4, background: 'rgba(255,255,255,0.3)', borderRadius: 2 }}>
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'var(--accent)', borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textAlign: 'center' }}>
        Passport Photo
        {photoUrl && <div style={{ fontSize: 10, color: 'var(--green)', fontWeight: 400 }}>✓ Saved in Drive</div>}
      </div>

      {error && <div style={{ fontSize: 11, color: 'var(--red)', textAlign: 'center', maxWidth: 120 }}>{error}</div>}

      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />

      <div style={{ display: 'flex', gap: 4 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ fontSize: 10, padding: '4px 10px' }}>
          📤 {photoUrl ? 'Change' : 'Upload'}
        </button>
        {photoUrl && (
          <button className="btn btn-ghost btn-sm" onClick={handleDelete} style={{ fontSize: 10, color: 'var(--red)', padding: '4px 8px' }}>🗑️</button>
        )}
      </div>
    </div>
  );
}
